<!-- =========================================================================================
  File Name: WizardStepOrigin.vue
  Description: Step 1 - Load visit points or skip to selection
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-10-30
========================================================================================== -->

<template>
	<div class="wizard-step-origin">
		<div class="step-header">
			<h2 class="step-title">{{ $t("Wizard.Steps.Origin.MainTitle") }}</h2>
			<p class="step-description">{{ $t("Wizard.Steps.Origin.Description") }}</p>
		</div>

		<div class="step-content">
			<!-- Option Cards -->
			<div class="options-grid">
				<!-- Quick Option: Load All -->
				<div class="option-card" :class="{selected: selectedOption === 'all'}" @click="selectOption('all')">
					<div class="option-icon">
						<feather-icon icon="ZapIcon" svgClasses="text-primary w-12 h-12" />
					</div>
					<h3 class="option-title">{{ $t("Wizard.Steps.Origin.LoadAll") }}</h3>
					<p class="option-description">{{ $t("Wizard.Steps.Origin.LoadAllDesc") }}</p>
					<div class="option-stats">
						<div class="stat-item">
							<feather-icon icon="MapPinIcon" svgClasses="w-4 h-4" />
							<span>{{ totalAvailablePoints }} {{ $t("Wizard.PointsAvailable") }}</span>
						</div>
						<div class="stat-item">
							<feather-icon icon="ClockIcon" svgClasses="w-4 h-4" />
							<span>{{ $t("Wizard.Instant") }}</span>
						</div>
					</div>
					<vs-button
						v-if="selectedOption === 'all'"
						class="option-action"
						color="success"
						type="gradient"
						icon-pack="feather"
						icon="icon-download"
						:disabled="isLoading"
						@click.stop="loadAllPoints"
					>
						{{ isLoading ? $t("Loading") : $t("Wizard.LoadAndContinue") }}
					</vs-button>
				</div>

				<!-- Skip Option: Select from Table Directly -->
				<div class="option-card" :class="{selected: selectedOption === 'skip'}" @click="selectOption('skip')">
					<div class="option-icon">
						<feather-icon icon="ListIcon" svgClasses="text-primary w-12 h-12" />
					</div>
					<h3 class="option-title">{{ $t("Wizard.Steps.Origin.SkipLoad") }}</h3>
					<p class="option-description">{{ $t("Wizard.Steps.Origin.SkipLoadDesc") }}</p>
					<div class="option-stats">
						<div class="stat-item">
							<feather-icon icon="FileTextIcon" svgClasses="w-4 h-4" />
							<span>{{ $t("Wizard.Steps.Origin.SelectFromTable") }}</span>
						</div>
						<div class="stat-item">
							<feather-icon icon="FilePlusIcon" svgClasses="w-4 h-4" />
							<span>{{ $t("Wizard.Steps.Origin.OrCreateManually") }}</span>
						</div>
					</div>
					<vs-button
						v-if="selectedOption === 'skip'"
						class="option-action"
						color="primary"
						type="gradient"
						icon-pack="feather"
						icon="icon-arrow-right"
						@click.stop="skipToSelection"
					>
						{{ $t("Wizard.Steps.Origin.ContinueToSelection") }}
					</vs-button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "WizardStepOrigin",
	props: {
		wizardData: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			selectedOption: null,
			isLoading: false,
			totalAvailablePoints: 0,
		};
	},
	computed: {
		previewPoints() {
			return this.wizardData.loadedPoints.slice(0, 10);
		},
	},
	mounted() {
		this.getTotalAvailablePoints();

		// If already have loaded points, show them
		if (this.wizardData.loadedPoints.length > 0) {
			this.selectedOption = this.wizardData.loadMethod || "all";
		}
	},
	methods: {
		/**
		 * Select loading option
		 */
		selectOption(option) {
			this.selectedOption = option;
		},

		/**
		 * Get total available points count
		 */
		async getTotalAvailablePoints() {
			try {
				const response = await this.$store.dispatch("moduleVisitPoint/findVisitPointPaginated", {
					page: 1,
					quantity: 1,
					order_by: ["id"],
					sort: "asc",
				});

				if (response && response.pagination) {
					this.totalAvailablePoints = response.pagination.total_records || 0;
				}
			} catch (error) {
				console.error("Error getting total points:", error);
			}
		},

		/**
		 * Load all points
		 */
		async loadAllPoints() {
			this.isLoading = true;
			this.$emit("update-data", {
				selectedPoints: [],
				loadMethod: "all",
			});
			try {
				const response = await this.$store.dispatch("moduleVisitPoint/findVisitPointPaginated", {
					page: 1,
					order_by: ["id"],
					sort: "asc",
				});

				if (response && response.records && response.records.length > 0) {
					const points = response.records.map((vp) => ({
						id: vp.id,
						identification: vp.identification || null,
						address: vp.address || null,
						latitude: parseFloat(vp.cy),
						longitude: parseFloat(vp.cx),
						demand: parseInt(vp.demand) || 0,
					}));

					// Update wizard data
					this.$emit("update-data", {
						loadedPoints: points,
						loadMethod: "all",
					});

					// Emit to parent to load points on map
					this.$parent.$emit("load-points-to-map", points);

					this.$vs.notify({
						color: "success",
						title: this.$t("Success"),
						text: this.$t("Wizard.Steps.Origin.LoadSuccess", {count: points.length}),
					});

					// Auto advance to next step after 1 second
					setTimeout(() => {
						this.$emit("next-step");
					}, 1000);
				} else {
					this.$vs.notify({
						color: "warning",
						title: this.$t("Warning"),
						text: this.$t("Wizard.Steps.Origin.NoPoints"),
					});
				}
			} catch (error) {
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: this.$t("Wizard.Steps.Origin.LoadError"),
				});
			} finally {
				this.isLoading = false;
			}
		},
		/**
		 * Skip to selection step
		 */
		skipToSelection() {
			// Don't load any points to map
			this.$emit("update-data", {
				loadedPoints: [],
				loadMethod: "skip",
			});

			// Advance to next step immediately
			this.$emit("next-step");
		},

		/**
		 * Truncate text
		 */
		truncate(text, length) {
			if (!text) return "-";
			return text.length > length ? text.substring(0, length) + "..." : text;
		},
	},
};
</script>

<style lang="scss" scoped>
.wizard-step-origin {
	.step-header {
		text-align: center;
		margin-bottom: 12px;

		.step-title {
			font-size: 1.5rem;
			font-weight: 700;
			color: #333;
			margin-bottom: 8px;
		}

		.step-description {
			font-size: 0.95rem;
			color: #666;
		}
	}

	.step-content {
		.options-grid {
			display: grid;
			grid-template-columns: repeat(1, 1fr);
			gap: 10px;
			margin-bottom: 24px;
			max-width: 900px;
			margin-left: auto;
			margin-right: auto;

			.option-card {
				background: #f8f9fa;
				border: 3px solid transparent;
				border-radius: 16px;
				padding: 32px;
				cursor: pointer;
				transition: all 0.3s ease;
				position: relative;
				overflow: hidden;

				&:hover {
					transform: translateY(-4px);
					box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
				}

				&.selected {
					border-color: var(--vs-primary);
					background: rgba(var(--vs-primary), 0.05);
				}

				.option-badge {
					position: absolute;
					top: 12px;
					left: 12px;
					background: #4caf50;
					color: white;
					padding: 4px 12px;
					border-radius: 12px;
					font-size: 0.75rem;
					font-weight: 600;
				}

				.option-icon {
					color: var(--vs-primary);
					margin-bottom: 20px;
					display: flex;
					justify-content: center;
				}

				.option-title {
					font-size: 1.5rem;
					font-weight: 700;
					color: #333;
					margin-bottom: 12px;
					text-align: center;
				}

				.option-description {
					color: #666;
					margin-bottom: 10px;
					text-align: center;
					min-height: 60px;
				}

				.option-stats {
					display: flex;
					flex-direction: column;
					gap: 8px;
					margin-bottom: 20px;

					.stat-item {
						display: flex;
						align-items: center;
						gap: 8px;
						color: #666;
						font-size: 0.9rem;
					}
				}

				.option-action {
					width: 100%;
				}
			}
		}

		.loaded-summary {
			margin-top: 32px;

			.summary-content {
				h4 {
					font-size: 1.25rem;
					margin-bottom: 8px;
				}

				p {
					margin-bottom: 16px;
				}

				.summary-actions {
					display: flex;
					gap: 12px;
					flex-wrap: wrap;
				}
			}
		}

		.points-preview {
			margin-top: 24px;
			background: white;
			border-radius: 8px;
			padding: 20px;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

			.preview-title {
				font-size: 1.1rem;
				font-weight: 600;
				margin-bottom: 16px;
			}

			.preview-table-wrapper {
				overflow-x: auto;

				.preview-table {
					width: 100%;
					border-collapse: collapse;

					thead {
						background: #f5f5f5;

						th {
							padding: 12px;
							text-align: left;
							font-weight: 600;
							color: #333;
							border-bottom: 2px solid #e0e0e0;
						}
					}

					tbody {
						tr {
							&:hover {
								background: #f9f9f9;
							}

							td {
								padding: 12px;
								border-bottom: 1px solid #e0e0e0;
								color: #666;
							}
						}
					}
				}
			}

			.preview-footer {
				margin-top: 12px;
				text-align: center;
				color: #666;
			}
		}
	}
}

// Transitions
.slide-down-enter-active,
.slide-down-leave-active {
	transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
	transform: translateY(-20px);
	opacity: 0;
}

.expand-enter-active,
.expand-leave-active {
	transition: all 0.3s ease;
	max-height: 600px;
}

.expand-enter,
.expand-leave-to {
	max-height: 0;
	opacity: 0;
}

// Responsive
@media screen and (max-width: 768px) {
	.wizard-step-origin {
		.step-content .options-grid {
			grid-template-columns: 1fr;
			max-width: 100%;
		}
	}
}

@media screen and (max-width: 1024px) {
	.wizard-step-origin {
		.step-content .options-grid {
			max-width: 700px;
		}
	}
}
</style>
